import React, { useState } from "react";
import {
    Box,
    Button,
    ColumnLayout,
    FormField,
    Modal,
    Select,
    SelectProps,
    SpaceBetween,
    Textarea,
} from "@amzn/awsui-components-react";
import ServiceCollection from "src/services/ServiceCollection";
import apiService from "src/services/ApiCallService";
import useLocalStorage from "src/utils/useLocalStorage";
import { TestedPartyMapping } from "src/models/tested-party-mapping/TestedPartyMapping";
import { TESTED_PARTY, OPERATION_UPDATE } from "./Constants";
import { NOTIFICATION_TYPES } from "src/utils/constants";
import { DbConjunctor, DbOperator } from "src/models/common/TPESearchRequest";

interface Props {
    visible: boolean;
    mapping: TestedPartyMapping | null;
    services: ServiceCollection;
    onDismiss: () => void;
    onSuccess: (updated?: TestedPartyMapping) => void;
}

/** Modal for reassigning the Tested Party (First/Second Party) on a CLI mapping with optional comment. */
export default function EditTestedPartyMappingModal(props: Props) {
    const { visible, mapping, services, onDismiss, onSuccess } = props;

    const firstPartyOption: SelectProps.Option = mapping
        ? { label: `First Party - ${mapping.firstPartyEntityName} (${mapping.firstPartyCompanyCode})`, value: "FIRST_PARTY" }
        : { label: "First Party", value: "FIRST_PARTY" };

    const secondPartyOption: SelectProps.Option = mapping
        ? { label: `Second Party - ${mapping.secondPartyEntityName} (${mapping.secondPartyCompanyCode})`, value: "SECOND_PARTY" }
        : { label: "Second Party", value: "SECOND_PARTY" };

    const isSecondParty =
        mapping?.testedPartyCompanyCode === mapping?.secondPartyCompanyCode &&
        mapping?.testedPartyEntityName === mapping?.secondPartyEntityName;

    const currentSelection = isSecondParty ? secondPartyOption : firstPartyOption;

    const [selectedTestedParty, setSelectedTestedParty] = useState<SelectProps.Option>(currentSelection);
    const [comment, setComment] = useState("");
    const [submitting, setSubmitting] = useState(false);

    // Reset state when modal is opened for a different row
    React.useEffect(() => {
        setSelectedTestedParty(currentSelection);
        setComment("");
    }, [mapping?.cliNumber, mapping?.testedPartyCompanyCode]);

    if (!mapping) {
        return null;
    }

    const deactivateTestedPartyTask = async (cliNumber: string) => {
        const searchResult: any = await (await apiService.searchTasks({
            page: 1,
            pageSize: 100,
            status: "Active",
            filterExpression: {
                conjunctor: DbConjunctor.AND,
                expressions: [
                    { attributeName: "notificationType", values: [NOTIFICATION_TYPES.ASSIGN_TESTED_PARTY], operator: DbOperator.EQUALS },
                    { attributeName: "entityId", values: [cliNumber], operator: DbOperator.EQUALS },
                ],
            },
        })).json();
        const matchingTasks = searchResult?.items ?? [];
        if (matchingTasks.length > 0) {
            await apiService.markNotificationsComplete({
                notifications: matchingTasks.map((t: any) => ({
                    owner: t.owner,
                    notificationId: t.taskId,
                })),
            });
        }
    };

    const handleSave = async () => {
        try {
            setSubmitting(true);
            const payload = {
                operation: OPERATION_UPDATE,
                source: "UI",
                comment,
                currentVersion: mapping.currentVersion, // optimistic locking per HLD §8.4
                updatedBy: useLocalStorage.getUsernameFromLocalStorage(),
                apttusCliData: {
                    providerEntityName: mapping.firstPartyEntityName,
                    providerCompanyCode: mapping.firstPartyCompanyCode,
                    recipientEntityName: mapping.secondPartyEntityName,
                    recipientCompanyCode: mapping.secondPartyCompanyCode,
                    agreementNumber: mapping.agreementNumber,
                    agreementName: mapping.agreementName,
                },
                reportingAttributes: {
                    testedParty: selectedTestedParty.value,
                },
            };
            const result = await apiService.addCliReportingConfig(OPERATION_UPDATE, mapping.cliNumber, TESTED_PARTY, payload);

            void deactivateTestedPartyTask(mapping.cliNumber).catch((err) =>
                console.warn('Failed to deactivate ASSIGN_TESTED_PARTY notification', err)
            );

            services.messageService.showSuccessAutoDismissBanner("Tested Party mapping updated successfully. Reports will reflect this change on the next report trigger.");
            onSuccess(result?.cliReportingConfig);
        } catch (error) {
            services.messageService.showErrorAutoDismissBanner("Failed to update Tested Party mapping");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Modal
            onDismiss={onDismiss}
            visible={visible}
            header={`Edit Tested Party Mapping — ${mapping.cliNumber}`}
            footer={
                <Box float="right">
                    <SpaceBetween direction="horizontal" size="xs">
                        <Button variant="link" onClick={onDismiss}>Cancel</Button>
                        <Button variant="primary" loading={submitting} onClick={handleSave}>Save</Button>
                    </SpaceBetween>
                </Box>
            }
        >
            <SpaceBetween direction="vertical" size="m">
                <ColumnLayout columns={2} variant="text-grid">
                    <div><Box variant="awsui-key-label">Agreement Number</Box>{mapping.agreementNumber}</div>
                    <div><Box variant="awsui-key-label">Agreement Name</Box>{mapping.agreementName}</div>
                    <div><Box variant="awsui-key-label">First Party (Provider)</Box>{mapping.firstPartyEntityName} ({mapping.firstPartyCompanyCode})</div>
                    <div><Box variant="awsui-key-label">Second Party (Recipient)</Box>{mapping.secondPartyEntityName} ({mapping.secondPartyCompanyCode})</div>
                </ColumnLayout>

                <FormField
                    label="Tested Party"
                    description="Select which party is the Tested Party for this CLI. Must be either First Party or Second Party."
                >
                    <Select
                        selectedOption={selectedTestedParty}
                        onChange={({ detail }) => setSelectedTestedParty(detail.selectedOption)}
                        options={[firstPartyOption, secondPartyOption]}
                    />
                </FormField>

                <FormField label="Comment" description="Optional reason for this change">
                    <Textarea
                        value={comment}
                        onChange={({ detail }) => setComment(detail.value)}
                        placeholder="Enter a reason for this change"
                    />
                </FormField>
            </SpaceBetween>
        </Modal>
    );
}
